import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Phone, Mail, Search, CheckCircle, XCircle, Send,
  Users, AlertTriangle, MessageCircle, Activity, Clock, X, User as UserIcon
} from 'lucide-react';

const API = 'https://api.lifeshare.app';

interface Stats {
  users: {
    total: number; verified: number; unverified: number;
    has_phone: number; phone_login_users: number; verification_rate: number;
  };
  recent_7d: { total: number; phone_registrations: number; email_registrations: number };
  whatsapp: { enabled: boolean; phone_number_id: string; token_configured: boolean; template: string };
  redis: { connected: boolean };
}

interface VerUser {
  id: number; email: string; username: string; full_name: string;
  phone: string | null; is_verified: boolean; is_active: boolean; created_at: string;
  avatar_url?: string;
}

const StatCard: React.FC<{ icon: React.ReactNode; value: number | string; label: string; color: string }> = ({ icon, value, label, color }) => {
  const colors: Record<string, string> = {
    green: 'from-green-50 to-green-100 border-green-200 text-green-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    gray: 'from-gray-50 to-gray-100 border-gray-200 text-gray-600',
    red: 'from-red-50 to-red-100 border-red-200 text-red-600',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
      <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
      <p className="text-xl font-semibold">{value}</p>
      <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const VerificationManagement: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<VerUser[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);
  const [selected, setSelected] = useState<VerUser | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [limit, setLimit] = useState(50);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/v1/admin/verification/stats`);
      setStats(data);
    } catch (e) { console.error(e); }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/api/v1/admin/verification/users`, {
        params: { filter_type: filter, search: search || undefined, limit: 200 }
      });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchUsers(); }, [filter, search]);
  useEffect(() => { if (stats) setLoading(false); }, [stats]);

  const handleTestWhatsApp = async () => {
    if (!testPhone.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/v1/admin/verification/test-whatsapp?phone=${encodeURIComponent(testPhone)}`);
      setTestResult(data);
    } catch (e: any) {
      setTestResult({ success: false, error: e.response?.data?.detail || 'Hata' });
    }
    setTesting(false);
  };

  const handleVerifyUser = async (userId: number) => {
    if (!window.confirm('Bu kullanıcıyı doğrulamak istediğinize emin misiniz?')) return;
    setActionLoading(userId);
    try {
      await axios.post(`${API}/api/v1/admin/verification/verify-user/${userId}`);
      setUsers(users.map(u => u.id === userId ? { ...u, is_verified: true } : u));
      if (selected?.id === userId) setSelected({ ...selected, is_verified: true });
      fetchStats();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Hata');
    }
    setActionLoading(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Doğrulama Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-1">Email, WhatsApp ve telefon doğrulama durumları</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <StatCard icon={<Users />} value={stats.users.total} label="Toplam" color="purple" />
            <StatCard icon={<CheckCircle />} value={stats.users.verified} label="Doğrulanmış" color="green" />
            <StatCard icon={<XCircle />} value={stats.users.unverified} label="Doğrulanmamış" color="red" />
            <StatCard icon={<Phone />} value={stats.users.has_phone} label="Telefonu Var" color="blue" />
            <StatCard icon={<Activity />} value={`%${stats.users.verification_rate}`} label="Oran" color="gray" />
          </div>
        )}

        {/* Service Status + WhatsApp Test */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Service Status */}
          {stats && (
            <div className="bg-white rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Servis Durumu</h3>
              </div>
              <div className="space-y-3">
                <StatusRow label="WhatsApp API" ok={stats.whatsapp.enabled} detail={stats.whatsapp.enabled ? `Phone ID: ${stats.whatsapp.phone_number_id}` : 'Devre dışı'} />
                <StatusRow label="WhatsApp Token" ok={stats.whatsapp.token_configured} detail={stats.whatsapp.token_configured ? 'Ayarlandı' : 'Eksik!'} />
                <StatusRow label="Template" ok={true} detail={stats.whatsapp.template} />
                <StatusRow label="Redis (OTP Store)" ok={stats.redis.connected} detail={stats.redis.connected ? 'Bağlı' : 'Bağlantı yok!'} />
              </div>
              {stats.recent_7d && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">Son 7 Gün Kayıtlar</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-blue-50 rounded-xl text-center">
                      <p className="text-lg font-semibold text-blue-700">{stats.recent_7d.email_registrations}</p>
                      <p className="text-[10px] text-blue-500">Email Kayıt</p>
                    </div>
                    <div className="p-2 bg-green-50 rounded-xl text-center">
                      <p className="text-lg font-semibold text-green-700">{stats.recent_7d.phone_registrations}</p>
                      <p className="text-[10px] text-green-500">Telefon Kayıt</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Test */}
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-gray-900">WhatsApp OTP Test</h3>
            </div>
            <p className="text-xs text-gray-500 mb-3">WhatsApp üzerinden test OTP kodu gönderin</p>
            <div className="flex gap-2">
              <input
                type="text" value={testPhone} onChange={e => setTestPhone(e.target.value)}
                placeholder="+905551234567"
                className="flex-1 px-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500"
              />
              <button onClick={handleTestWhatsApp} disabled={testing || !testPhone.trim()}
                className="px-5 py-3 bg-green-600 text-white rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-green-700 disabled:opacity-50 transition-colors">
                {testing ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {testing ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
            {testResult && (
              <div className={`mt-3 p-3 rounded-xl text-sm ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {testResult.success ? (
                  <div className="space-y-1">
                    <p className="font-medium text-green-700">Başarıyla gönderildi!</p>
                    <p className="text-green-600">Kod: <span className="font-mono font-bold text-lg">{testResult.code}</span></p>
                    <p className="text-xs text-green-500">Dil: {testResult.language} | ID: {testResult.message_id?.slice(0, 20)}...</p>
                  </div>
                ) : (
                  <p className="text-red-700">Hata: {testResult.error}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl p-4 mb-6">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="İsim, e-posta veya telefon ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {([
              { key: 'all', label: 'Tümü' },
              { key: 'unverified', label: 'Doğrulanmamış' },
              { key: 'phone', label: 'Telefonu Var' },
              { key: 'no_phone', label: 'Telefonu Yok' },
            ] as const).map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.key ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {f.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-500">{total} sonuç</span>
          </div>
        </div>

        {/* Users Grid - Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.slice(0, limit).map(u => (
            <div
              key={u.id}
              className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-200 transition-all cursor-pointer"
              onClick={() => setSelected(u)}
            >
              {/* Header */}
              <div className="flex items-start gap-3 mb-3">
                {u.avatar_url ? (
                  <img src={u.avatar_url.startsWith('/') ? `${API}${u.avatar_url}` : u.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-600 font-semibold text-lg">{(u.full_name || u.username || '?').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{u.full_name || u.username}</p>
                  <p className="text-xs text-gray-500 truncate">@{u.username}</p>
                </div>
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${u.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
              </div>

              {/* Email */}
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                <Mail className="w-3.5 h-3.5" />
                <span className="truncate">{u.email}</span>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                <Phone className="w-3.5 h-3.5" />
                {u.phone ? (
                  <span className="font-mono text-green-700 bg-green-50 px-2 py-0.5 rounded">{u.phone}</span>
                ) : (
                  <span className="text-gray-400">Telefon yok</span>
                )}
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${u.is_verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  {u.is_verified ? '✓ Doğrulanmış' : '✕ Doğrulanmamış'}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${u.is_active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                  {u.is_active ? 'Aktif' : 'Pasif'}
                </span>
                {u.phone && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">📱 Telefon</span>}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <UserIcon className="w-3 h-3" />
                  <span>#{u.id}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>{u.created_at ? new Date(u.created_at).toLocaleDateString('tr-TR') : '—'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        {users.length > limit && (
          <div className="text-center mt-6">
            <button onClick={() => setLimit(l => l + 50)} className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors">
              Daha fazla yükle ({limit} / {users.length})
            </button>
          </div>
        )}

        {/* Empty */}
        {users.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center">
            <ShieldCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Kullanıcı bulunamadı</p>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl max-h-[90vh] overflow-auto rounded-t-3xl" onClick={e => e.stopPropagation()}>

            {/* Handle */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-9 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-3">
                {selected.avatar_url ? (
                  <img src={selected.avatar_url.startsWith('/') ? `${API}${selected.avatar_url}` : selected.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-purple-600 font-semibold text-xl">{(selected.full_name || selected.username || '?').charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900">{selected.full_name || selected.username}</p>
                  <p className="text-sm text-gray-500">@{selected.username}</p>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Status */}
            <div className="px-5 py-3 flex flex-wrap gap-2">
              <span className={`px-2.5 py-1 rounded-xl text-xs font-medium ${selected.is_verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                {selected.is_verified ? '✓ Doğrulanmış' : '✕ Doğrulanmamış'}
              </span>
              <span className={`px-2.5 py-1 rounded-xl text-xs font-medium ${selected.is_active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                {selected.is_active ? '✓ Aktif' : '✕ Pasif'}
              </span>
              {selected.phone && <span className="px-2.5 py-1 rounded-xl text-xs font-medium bg-purple-100 text-purple-700">📱 Telefon Var</span>}
            </div>

            {/* Info */}
            <div className="px-5 pb-4 space-y-3">
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">E-posta</p>
                <p className="text-sm text-gray-900">{selected.email}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Telefon</p>
                <p className="text-sm text-gray-900">{selected.phone || 'Belirtilmemiş'}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Kullanıcı ID</p>
                  <p className="text-sm font-mono text-gray-900">#{selected.id}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Kayıt Tarihi</p>
                  <p className="text-sm text-gray-900">{selected.created_at ? new Date(selected.created_at).toLocaleDateString('tr-TR') : '—'}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-5 border-t border-gray-100 space-y-2">
              {!selected.is_verified ? (
                <button
                  onClick={() => handleVerifyUser(selected.id)}
                  disabled={actionLoading === selected.id}
                  className="w-full py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-green-700 transition-colors"
                >
                  {actionLoading === selected.id ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Doğrula
                </button>
              ) : (
                <div className="w-full py-2.5 bg-green-50 text-green-700 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5">
                  <CheckCircle className="w-4 h-4" />
                  Zaten Doğrulanmış
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function StatusRow({ label, ok, detail }: { label: string; ok: boolean; detail: string }) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2">
        <div className={`w-2.5 h-2.5 rounded-full ${ok ? 'bg-green-500' : 'bg-red-500'}`} />
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <span className={`text-xs font-mono px-2 py-0.5 rounded ${ok ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{detail}</span>
    </div>
  );
}

export default VerificationManagement;
